import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Passenger, PnrInfo } from 'src/app/core/models/itinerary.model';
import { ItineraryService } from 'src/app/search-result/services/itinerary.service';
import { State } from 'src/app/app.reducer';
import { Observable, Subscription } from 'rxjs';
import { SeatsService } from 'src/app/search-result/services/seats.service';
import { REGEX_CODES, TypeOfAisleSeat } from 'src/app/core/enums/enums';
import {
  ReservationSeat,
  SeatMap,
  SeatSelectedEnum,
  SeatSelectedByPassenger,
  SeatSelectedOption,
  ICancelSeatRequestService,
  ReserveSeatConfirmation,
  PassengerSeatPrice,
  IReservationSeatSeatPrice,
  ICabinRowSeatOfferTaxes,
  IReservationSeatSeatPriceTaxes
} from 'src/app/core/models/seat.model';

@Component({
  selector: 'app-seat-map',
  templateUrl: './seat-map.component.html',
  styleUrls: ['./seat-map.component.scss']
})
export class SeatMapComponent implements OnInit, OnDestroy {
  @Input() flightNumber: number;
  @Input() seatMap: SeatMap;
  @Input() loadingSeatReserve: boolean;
  @Input() passengersSelected: Passenger[];

  @Output() seatMapSelection = new EventEmitter<ReservationSeat>();
  @Output() removeSeatsOption = new EventEmitter<any>();
  @Output() hideSeatMap = new EventEmitter();

  store$: Observable<State>;
  pnrInfo: PnrInfo;
  seatListsReserved$: Observable<ReservationSeat[]>;
  reserveSeatResult$: Observable<ReserveSeatConfirmation>;

  subscriptions = new Subscription();

  selectedSeats: SeatSelectedOption[] = [];
  loadingSeatCancelation = false;
  needCancelSeat = false;
  passengerId: string;
  preReserveSeat: string;
  leftSideOfAircraft: string;
  rightSideOfAircraft: string;
  noSeatAtThisLocation: string;
  centralSideOfAircraft: string;
  aisleSeat: string;
  preReserveServiceByPaxWhenSeatReserve = 'preReserveSeat';

  constructor(
    private itService: ItineraryService,
    private store: Store<State>,
    private seatsService: SeatsService
  ) {}

  ngOnInit(): void {
    this.callComponentSubscriptions();
    this.assignTypeOfAisleSeat();
    this.filterPreReserveSeat();

    this.store$ = this.store.pipe();
    this.reserveSeatResult$ = this.seatsService.getReserveSeatResult();
    this.seatListsReserved$ = this.seatsService.getSeatListToReserve();
  }

  callComponentSubscriptions() {
    this.subscriptions.add(
      this.itService
        .getPnrInfo()
        .pipe(take(1))
        .subscribe((res) => {
          if (res) {
            this.pnrInfo = res;
          }
        })
    );
  }

  assignTypeOfAisleSeat() {
    this.leftSideOfAircraft = TypeOfAisleSeat.LeftSideOfAircraft;
    this.rightSideOfAircraft = TypeOfAisleSeat.RightSideOfAircraft;
    this.noSeatAtThisLocation = TypeOfAisleSeat.NoSeatAtThisLocation;
    this.centralSideOfAircraft = TypeOfAisleSeat.CentralSideOfAircraft;
    this.aisleSeat = TypeOfAisleSeat.AisleSeat;
  }

  filterPreReserveSeat() {
    this.preReserveSeat = this.pnrInfo.preReserveServicesByPax.filter((prbp) =>
      prbp.preReserveServices.filter((rs) => rs.type === this.preReserveServiceByPaxWhenSeatReserve)
    )[0]?.preReserveServices[0].code;
  }

  reduceSeatTaxes(taxes: ICabinRowSeatOfferTaxes[]): IReservationSeatSeatPriceTaxes[] {
    const reduced = taxes?.reduce((acc, currTax) => {
      acc.push({
        taxCode: currTax?.taxCode,
        currencyCode: currTax?.totalAmount?.currencyCode,
        totalAmount: currTax?.totalAmount?.totalAmount
      });

      return acc;
    }, [] as IReservationSeatSeatPriceTaxes[]);

    return reduced;
  }

  getReservationSeatSeatPrice(event: SeatSelectedByPassenger): IReservationSeatSeatPrice {
    const numberMatch = event?.seatSelected?.seat?.match(REGEX_CODES.matchNumbersStrings);

    const rowNumber = numberMatch[0];
    const seatNumber = numberMatch[1];

    const reduceSeatPrice = this.seatMap?.cabin?.row?.reduce((acc, currRow) => {
      if (currRow?.rowNumber === rowNumber) {
        acc = currRow?.seat.reduce((acc, currSeat) => {
          if (currSeat?.number === seatNumber) {
            const reduced: IReservationSeatSeatPrice = {
              totalAmount: {
                price: currSeat?.offer?.price,
                taxes: this.reduceSeatTaxes(currSeat?.offer?.taxes)
              }
            };

            acc = reduced;
          }

          return acc;
        }, {} as IReservationSeatSeatPrice);
      }

      return acc;
    }, {} as IReservationSeatSeatPrice);

    return reduceSeatPrice;
  }

  onChangeSeat(event: SeatSelectedByPassenger): void {
    const seatToPassenger = this.selectedSeats.find(
      (seat) => seat.passengerId === event.passengerId
    );
    const seatIsSelected = this.selectedSeats.find((seat) => seat.seat === event.seatSelected.seat);

    if (event.seatSelected.action === SeatSelectedEnum.add) {
      seatToPassenger &&
        (this.selectedSeats = this.selectedSeats.filter(
          (seat) => seat.passengerId !== event.passengerId
        ));
      seatIsSelected &&
        (this.selectedSeats = this.selectedSeats.filter(
          (seat) => seat.seat !== event.seatSelected.seat
        ));

      this.selectedSeats.push({
        seat: event.seatSelected.seat,
        passengerId: event.passengerId,
        seatPrice: this.getReservationSeatSeatPrice(event)
      });
    }

    if (event.seatSelected.action === SeatSelectedEnum.remove) {
      this.selectedSeats = this.selectedSeats.filter(
        (seat) => seat.seat !== event.seatSelected.seat && seat.passengerId !== event.passengerId
      );
    }
  }

  selectNewSeat(): void {
    this.selectedSeats.forEach((option) => {
      const seatSeleceted = {
        boardingPass: false,
        nameSelect: {
          nameNumber: String(option.passengerId)
        },
        seatSelect: {
          number: option.seat
        },
        segmentSelect: {
          number: String(this.flightNumber)
        },
        seatPrice: option.seatPrice
      } as ReservationSeat;
      this.seatMapSelection.emit(seatSeleceted);
    });
    this.selectedSeats = [];
    this.hideSeatMap.emit();
  }

  removeSeatSelected(event: string): void {
    const cancelSeat: ICancelSeatRequestService = {
      pnr: this.pnrInfo.code,
      all: 'true',
      seats: [{ seatNumber: event, segmentNumber: String(this.flightNumber) }]
    };
    this.seatsService.dispatchCancelSeat(this.pnrInfo.gds, cancelSeat);
  }

  GetPosition(seat, AisleSeat: TypeOfAisleSeat, sideOfAircraft: TypeOfAisleSeat): boolean {
    let existAisleSeat =
      seat.listOflocation.filter((position) => position === AisleSeat).length > 0;
    let existsideOfAircraft =
      seat.listOflocation.filter((position) => position === sideOfAircraft).length > 0;
    return existAisleSeat && existsideOfAircraft;
  }

  GetNotPositionLeft(seat, sideOfAircraft: TypeOfAisleSeat, previewPositionSeat): boolean {
    let existNoPosition =
      seat.listOflocation.filter((position) => position === sideOfAircraft).length > 0;
    if (!previewPositionSeat) {
      return false;
    }
    let existPreviewPosition =
      previewPositionSeat.listOflocation.filter(
        (position) => position === this.noSeatAtThisLocation
      ).length > 0;
    return existNoPosition && existPreviewPosition;
  }

  GetNotPositionRight(seat, sideOfAircraft: TypeOfAisleSeat, nextPosition): boolean {
    let existNoPosition =
      seat.listOflocation.filter((position) => position === sideOfAircraft).length > 0;
    if (!nextPosition) {
      return false;
    }
    let existPreviewPosition =
      nextPosition.listOflocation.filter((position) => position === this.noSeatAtThisLocation)
        .length > 0;
    return existNoPosition && existPreviewPosition;
  }

  resetSelectedSeat(event: string): void {
    let passenger;
    this.seatListsReserved$.pipe(take(1)).subscribe((list) => {
      passenger = list.find((option) => option.nameSelect.nameNumber === event);
    });
    this.removeSeatsOption.emit({
      seatNumber: passenger.seatSelect.number,
      seatName: passenger.nameSelect.nameNumber,
      index: passenger.segmentSelect.number
    });
  }

  getSeatSelected(id: string) {
    const assignSeat = this.selectedSeats.find((o) => o.passengerId === id);
    return assignSeat ? assignSeat.seat : this.preReserveSeat ? this.preReserveSeat : '-';
  }

  getHasReservedSeatsInfo(name: string): boolean {
    return this.seatMap.preReservedSeats.filter((seat) => seat.passenger === name).length > 0;
  }

  getPreReservedSeatsTextInfo(name: string): string {
    const preReservedSeatsInfo: PassengerSeatPrice = this.seatMap?.preReservedSeats
      .filter((seat) => seat.passenger === name)
      .pop();

    return preReservedSeatsInfo
      ? `${preReservedSeatsInfo?.seatSelected}, ${preReservedSeatsInfo?.price} ${preReservedSeatsInfo?.currency}`
      : 'NO INFO';
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
